import { applyMiddleware, compose, createStore } from 'redux';
import { apiMiddleware } from 'redux-api-middleware';
// imports for router
import { redirect, connectRoutes } from 'redux-first-router';
import routesMap from '../routesMap';

import createSagaMiddleware from 'redux-saga';
import queryString from 'query-string';
import { find } from 'lodash';

// redux sagas, reducers, actions
import sagas from './sagas';
import composeReducers from './rootReducer';
import { boot } from 'modules/app';
import { folderQueryMiddleware } from 'modules/folders/folderQueryMiddleware';
import { displayUnsavedChangesPrompt } from 'modules/tdosTable/unsavedChangesGuard';

import * as Sentry from '@sentry/react';
import { thunk } from 'redux-thunk';

// set once the store exists; the router's leave prompt dispatches through it
let appDispatch: ((action: unknown) => unknown) | undefined;

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  // The full state holds the session token (auth.sessionToken, user.user.token) and is
  // hundreds of KB, over Sentry's event size limit. Send only the current route.
  stateTransformer: (state: {
    location?: { type?: string; pathname?: string };
  }) => ({
    location: {
      type: state?.location?.type,
      pathname: state?.location?.pathname,
    },
  }),
  // action payloads can hold tokens or user data; keep the type only
  actionTransformer: (action: { type: string }) => ({ type: action.type }),
});
const {
  reducer: routerReducer,
  middleware: routerMiddleware,
  enhancer: routerEnhancer,
  initialDispatch,
} = connectRoutes(routesMap, {
  querySerializer: queryString,
  initialDispatch: false,
  onBeforeChange: (dispatch, getState, { action }: any) => {
    const routeDefinition = routesMap[action.type as keyof typeof routesMap];

    if (routeDefinition && routeDefinition.redirects) {
      const matchedRedirect = find(
        routeDefinition.redirects,
        ({ test }) => !!test(getState, action)
      );

      matchedRedirect && dispatch(redirect(matchedRedirect.to));
    }
  },
  displayConfirmLeave: displayUnsavedChangesPrompt(() => appDispatch),
});

const sagaMiddleware = createSagaMiddleware();

// VE-27161: folderQueryMiddleware must see route actions before the router.
const middlewares = [
  folderQueryMiddleware,
  routerMiddleware,
  apiMiddleware,
  sagaMiddleware,
  thunk,
];

const isDev = process.env.NODE_ENV === 'development';
if (isDev || localStorage.getItem('illuminate-log') === 'true') {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { createLogger } = require('redux-logger');
  middlewares.push(
    createLogger({
      collapsed: true,
    })
  );
}

const composeMiddlewares = applyMiddleware(...middlewares);
const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

export default function configureStore() {
  const store = createStore(
    composeReducers({ location: routerReducer }),
    composeEnhancers(routerEnhancer, composeMiddlewares, sentryReduxEnhancer)
  ) as any;
  appDispatch = store.dispatch;

  sagaMiddleware.run(sagas);
  initialDispatch?.();
  store.dispatch(boot());

  if (typeof window !== 'undefined' && (window as any).Cypress) {
    (window as any).store = store;
  }

  return store;
}
